// @ts-nocheck
// TODO: Fix ts type for theme
import React, { useState } from "react";
import { createLocalStorageRecentSearchesPlugin } from "@algolia/autocomplete-plugin-recent-searches";
import { AutocompleteOptions, AutocompleteState, createAutocomplete, BaseItem, OnSelectParams } from "@algolia/autocomplete-core";
import Typesense from "typesense";
import { SearchResponseAdapter } from "typesense-instantsearch-adapter/lib/SearchResponseAdapter";

import { Button, ButtonProps, IconButton, InputAdornment, TextField, OutlinedTextFieldProps } from "@mui/material";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import { Cancel as ClearIcon, Search as SearchIcon } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";

import { commonSearchTermsList } from "./terms/common-searches";
import { SearchTermHitItem } from "./search-term-hit-item";
import { AutocompleteItem, SearchItem } from "./types";
import { addHighlightedAttribute } from "./add-highlights";
import useElementSize from "../../use-element-size/";

import { TypesenseServerSettings } from "../../search/search-client";

// TypeSense collection
const collectionName: string = typeof window.__searchSuggestionsIndexName === "string" ? window.__searchSuggestionsIndexName : null;

// TypeSense client for search suggestions
const search_response_adapter = (result) => new SearchResponseAdapter(result, { params: {} }, { geoLocationField: "_geoloc" });

const typesense_client = () =>
  new Typesense.Client({
    ...TypesenseServerSettings,
    connectionTimeoutSeconds: 2,
  });

const client = typesense_client();

// Styling
const useStyles = makeStyles((theme) => ({
  iconColor: {
    color: theme.palette.secondary,
  },
  searchRow: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    marginBlockEnd: 0,
    "& .input-search": {
      borderTopRightRadius: 0,
      borderBottomRightRadius: 0,
    },
    "& .btn-search-submit": {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      boxShadow: "none",
      minHeight: "unset",
    },
  },
}));

// To adjust button when state changes
interface SearchButtonProps extends ButtonProps {
  isopen: boolean;
}

const SearchButton = styled(Button, {
  shouldForwardProp: (prop) => true,
})<SearchButtonProps>(({ isopen, theme }) => {
  return {
    textTransform: "none",
    borderTopRightRadius: "25px",
    borderBottomRightRadius: "25px",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    backgroundColor: theme.palette.common.black,
    "&:hover": {
      backgroundColor: theme.palette.common.darkblue,
    },
    minWidth: "28%",
    [theme.breakpoints.up("sm")]: {
      minWidth: "20%",
    },
    [theme.breakpoints.up("md")]: {
      minWidth: "16%",
    },
    [theme.breakpoints.up("lg")]: {
      minWidth: "14%",
    },
    ...(isopen && {
      borderBottomRightRadius: 0,
    }),
  };
});

// Textfield for search
interface SearchTextFieldProps extends OutlinedTextFieldProps {
  isopen: boolean;
}

const SearchTextField = styled(TextField, {
  shouldForwardProp: (prop) => true,
})<SearchTextFieldProps>(({ isopen, theme }) => {
  return {
    "& .MuiOutlinedInput-root": {
      fieldset: {
        border: "1px solid #000",
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        borderTopLeftRadius: "25px",
        borderBottomLeftRadius: "25px",
        ...(isopen && {
          borderBottomLeftRadius: 0,
        }),
      },
    },
  };
});

// Autocomplete plugin for recent searches
const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
  key: "RECENT_SEARCH",
  limit: 3,
  transformSource({ source }) {
    return {
      ...source,
      sourceId: "recentSearches",
      templates: {
        header() {
          return (
            <>
              <span className="aa-SourceHeaderTitle">Tidigare sökningar</span>
              <div className="aa-SourceHeaderLine" />
            </>
          );
        },
        item({ item }) {
          return <SearchTermHitItem item={item} highlightAttribute="label" />;
        },
      },
    };
  },
});

// Debounce for autocomplete sources
const debouncePromise = (fn: Function, time: number) => {
  let timerId: NodeJS.Timeout;

  return function debounced(...args: any) {
    if (timerId) {
      clearTimeout(timerId);
    }

    return new Promise((resolve) => {
      timerId = setTimeout(() => resolve(fn(...args)), time);
    });
  };
};

const debounced = debouncePromise((items: BaseItem[]) => Promise.resolve(items), 300);

interface AutocompleteProps extends Partial<AutocompleteOptions<AutocompleteItem>> {
  initialStateQuery: string | undefined;
  onSubmitCallback: Function;
  onResetCallback?: Function;
}

// interface SearchItem extends BaseItem {
//   label: string;
// }

export const CustomAutocomplete = (props: AutocompleteProps) => {
  const { onSubmitCallback, onResetCallback, initialStateQuery = "" } = props;
  const classes = useStyles();
  const [autocompleteState, setAutocompleteState] = useState<AutocompleteState<AutocompleteItem>>({
    collections: [],
    completion: null,
    context: {},
    isOpen: false,
    query: "",
    activeItemId: null,
    status: "idle",
  });

  // Create autocomplete
  const autocomplete = React.useMemo(
    () =>
      createAutocomplete<AutocompleteItem, React.BaseSyntheticEvent, React.MouseEvent, React.KeyboardEvent>({
        initialState: {
          query: initialStateQuery,
        },
        onStateChange({ state }) {
          setAutocompleteState(state);
        },
        onSubmit: ({ event, state, ...rest }) => {
          event.stopPropagation();
          event.preventDefault();
          if (typeof onSubmitCallback === "function") {
            onSubmitCallback(state);
          }
        },
        openOnFocus: false,
        plugins: [recentSearchesPlugin],
        getSources() {
          // Debounce all sources
          return debounced([
            {
              // Popular search searches, shown when query field is empty
              sourceId: "popularSearches", // Top search terms
              onSelect({ item, setQuery, setIsOpen, refresh }: OnSelectParams<SearchItem>) {
                setQuery(item.label);
                setIsOpen(false);
                refresh();
              },
              getItems({ query }: { query: string }) {
                if (query === "") {
                  return commonSearchTermsList;
                }
                return [];
              },
              getItemInputValue: ({ item }: { item: BaseItem }) => {
                return item.label;
              },
              templates: {
                header() {
                  return (
                    <>
                      <span className="aa-SourceHeaderTitle">Vanliga sökningar</span>
                      <div className="aa-SourceHeaderLine" />
                    </>
                  );
                },
                item({ item }: { item: SearchItem }) {
                  return <SearchTermHitItem item={item} highlightAttribute="label" />;
                },
              },
            },
            {
              // Fetch from Typesense
              sourceId: "typesense",
              onSelect({ item, setQuery, setIsOpen, refresh }: OnSelectParams<SearchItem>) {
                setQuery(item.label);
                setIsOpen(false);
                refresh();
              },
              getItems: ({ query }: { query: string }) => {
                if (query === "") {
                  return [];
                }
                return client
                  .collections(collectionName)
                  .documents()
                  .search({
                    q: query,
                    query_by: "label",
                    per_page: 12,
                    highlight_full_fields: "label",
                    highlight_start_tag: "__aa-highlight__",
                    highlight_end_tag: "__/aa-highlight__",
                  })
                  .then((result) =>
                    search_response_adapter(result)
                      .adapt()
                      .hits.map((item: AutocompleteItem) => addHighlightedAttribute({ item, query }))
                  );
              },
              getItemInputValue: ({ item }: { item: SearchItem }) => {
                return item.label;
              },
              templates: {
                header() {
                  return (
                    <>
                      <span className="aa-SourceHeaderTitle">Sökförslag</span>
                      <div className="aa-SourceHeaderLine" />
                    </>
                  );
                },
                item({ item }: { item: SearchItem }) {
                  return <SearchTermHitItem item={item} highlightAttribute="label" />;
                },
              },
            },
            // Reading terms from static file
            // {
            //   sourceId: "searchTerms", // The suggested search terms
            //   getItems({ query }) {
            //     if (query === "") {
            //       return [];
            //     }
            //     if (!searchTermsList) {
            //       return;
            //     }
            //     return searchTermsList
            //       .filter((item) => item?.label.toLocaleLowerCase().includes(query.toLocaleLowerCase()))
            //       .map((item) => addHighlightedAttribute({ item, query }));
            //   },
            //   onSelect({ item, setQuery, setIsOpen, refresh }) {
            //     setQuery(item.label);
            //     setIsOpen(false);
            //     refresh();
            //   },
            //   templates: {
            //     header() {
            //       return (
            //         <>
            //           <span className="aa-SourceHeaderTitle">Sökförslag</span>
            //           <div className="aa-SourceHeaderLine" />
            //         </>
            //       );
            //     },
            //     item({ item }) {
            //       return <SearchTermHitItem item={item} highlightAttribute="label" />;
            //     },
            //   },
            // },
          ]);
        },
        ...props,
      }),
    [props, initialStateQuery, onSubmitCallback]
  ); // End autocomplete

  const inputRef = React.useRef<HTMLInputElement>(null);
  const formRef = React.useRef<HTMLFormElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  const { getEnvironmentProps } = autocomplete;

  // Handle form actions
  const handleClickAway = () => {
    autocomplete.setIsOpen(false);
  };

  const handleReset = () => {
    autocomplete.setQuery("");
    autocomplete.refresh();
    if (typeof onResetCallback === "function") {
      onResetCallback("");
    }
  };

  // Setup refs
  React.useEffect(() => {
    if (!formRef.current || !panelRef.current || !inputRef.current) {
      return undefined;
    }

    const { onTouchStart, onTouchMove } = getEnvironmentProps({
      formElement: formRef.current,
      inputElement: inputRef.current,
      panelElement: panelRef.current,
    });

    window.addEventListener("touchstart", onTouchStart);
    window.addEventListener("touchmove", onTouchMove);

    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
    };
  }, [getEnvironmentProps, formRef, inputRef, panelRef]);

  // Get dimensions of form to be able to set same width for the autocomplete suggestions panel
  let formDimensions = useElementSize(formRef); // useContainerDimensions

  // Halt if no search suggestion index has been set
  if (!collectionName) {
    return <div>No search suggestion index set.</div>;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <div {...autocomplete.getRootProps({})}>
        <form className={classes.searchRow} ref={formRef} {...autocomplete.getFormProps({ inputElement: inputRef.current })} p>
          <SearchTextField
            isopen={autocompleteState.isOpen}
            fullWidth={true}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "50px",
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            }}
            variant="outlined"
            size={"small"}
            error={false}
            color="info"
            label="Sökord"
            ref={inputRef}
            // value={autocompleteState}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton aria-label="Rensea sökfält" onClick={handleReset} edge="end" className={classes.iconColor}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            {...autocomplete.getInputProps({ inputElement: inputRef.current })}
          />

          <SearchButton isopen={autocompleteState.isOpen} type="submit" className={`btn-black`} variant="contained" startIcon={<SearchIcon />}>
            Sök
          </SearchButton>
        </form>

        {autocompleteState.isOpen && (
          <div
            ref={panelRef}
            className={["aa-Panel", "aa-Panel--desktop", autocompleteState.status === "stalled" && "aa-Panel--stalled"].filter(Boolean).join(" ")}
            {...autocomplete.getPanelProps({})}
            style={{ width: formDimensions.width }}
          >
            <div className="aa-PanelLayout aa-Panel--scrollable">
              {autocompleteState.collections.map((collection, index) => {
                const { source, items } = collection;

                return (
                  <section key={`source-${index}`} className="aa-Source">
                    {items.length > 0 && (
                      <>
                        {source?.templates?.header && source.templates.header()}

                        <ul className="aa-List" {...autocomplete.getListProps()}>
                          {items.map((item, indx) => {
                            return (
                              <li key={`st-${indx}`} className="aa-Item" {...autocomplete.getItemProps({ item, source })}>
                                {source?.templates?.item && source.templates.item({ item })}
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                  </section>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </ClickAwayListener>
  );
};
